import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BellOff, CheckCircle, AlertCircle, ArrowLeft, Bell } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrackerUnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState('confirm'); // confirm | processing | done | error | resubscribed
  const [subscriber, setSubscriber] = useState(null);

  useEffect(() => {
    if (!token) { setStatus('error'); return; }
    fetch(`${API}/api/tracker/me?token=${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setSubscriber(d); if (!d.email_preferences?.enabled) setStatus('already'); })
      .catch(() => setStatus('error'));
  }, [token]);

  const handleUnsubscribe = async () => {
    setStatus('processing');
    try {
      const res = await fetch(`${API}/api/tracker/unsubscribe?token=${token}`, { method: 'POST' });
      if (!res.ok) throw new Error();
      setStatus('done');
    } catch { setStatus('error'); }
  };

  const handleResubscribe = async () => {
    try {
      const res = await fetch(`${API}/api/tracker/resubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!res.ok) throw new Error();
      setStatus('resubscribed');
    } catch { setStatus('error'); }
  };

  return (
    <PageLayout>
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="tracker-unsubscribe-page">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center">

          {status === 'confirm' && subscriber && (
            <>
              <BellOff className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Unsubscribe from emails?</h1>
              <p className="text-sm text-slate-500 mb-1">
                You will stop receiving weekly digest emails at:
              </p>
              <p className="text-sm font-semibold text-taxera-charcoal mb-6">{subscriber.email}</p>
              <p className="text-xs text-slate-400 mb-6">
                Your tracked countries and data will be kept. You can re-enable emails anytime from your tracker dashboard.
              </p>
              <button
                onClick={handleUnsubscribe}
                className="w-full py-2.5 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition-colors mb-3"
                data-testid="confirm-unsubscribe-btn"
              >
                Yes, unsubscribe me
              </button>
              <Link
                to={`/tracker/my?token=${token}`}
                className="block text-xs font-semibold text-slate-400 hover:text-taxera-blue transition-colors"
              >
                Cancel — go to my tracker
              </Link>
            </>
          )}

          {status === 'processing' && (
            <>
              <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-taxera-blue animate-spin mx-auto mb-4" />
              <p className="text-sm text-slate-500">Processing...</p>
            </>
          )}

          {status === 'done' && (
            <>
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Unsubscribed</h1>
              <p className="text-sm text-slate-500 mb-6">
                You will no longer receive email digests. Your tracked countries are still saved.
              </p>
              <button
                onClick={handleResubscribe}
                className="w-full py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors mb-3"
                data-testid="resubscribe-btn"
              >
                Changed your mind? Re-subscribe
              </button>
              <Link to="/tracker" className="block text-xs font-semibold text-slate-400 hover:text-taxera-blue transition-colors">
                Go to Global Tax Tracker
              </Link>
            </>
          )}

          {status === 'already' && (
            <>
              <BellOff className="w-10 h-10 text-slate-400 mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Already unsubscribed</h1>
              <p className="text-sm text-slate-500 mb-6">Email notifications are already disabled for your account.</p>
              <button
                onClick={handleResubscribe}
                className="w-full py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors mb-3"
                data-testid="resubscribe-btn"
              >
                Re-enable email notifications
              </button>
              <Link to="/tracker" className="block text-xs font-semibold text-slate-400 hover:text-taxera-blue transition-colors">
                Go to Global Tax Tracker
              </Link>
            </>
          )}

          {status === 'resubscribed' && (
            <>
              <Bell className="w-10 h-10 text-taxera-teal mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Re-subscribed</h1>
              <p className="text-sm text-slate-500 mb-6">You will receive weekly digest emails again.</p>
              <Link
                to={`/tracker/my?token=${token}`}
                className="inline-block px-6 py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors"
              >
                Open My Tracker
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Something went wrong</h1>
              <p className="text-sm text-slate-500 mb-6">
                {!token ? 'No token provided. This link may be invalid.' : 'The link may have expired. Request a new access link from the tracker.'}
              </p>
              <Link
                to="/tracker/my"
                className="inline-block px-6 py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors"
              >
                Go to My Tracker
              </Link>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
